import React, { useMemo, useEffect } from "react";
import { Form, Modal, Input, FormInstance } from "antd";
import { FrontDeskBooking } from "../../../types";
import { RadioCardGroup, CheckboxCardGroup } from "./RadioCardGroup";
import "../../rooms/Rooms.css"; // For grid-two class

interface AllocateModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  onSubmit: (values: any) => void;
  selectedBooking: FrontDeskBooking | null;
  rooms: any[];
  bookings?: FrontDeskBooking[];
  loading?: boolean;
}

export const AllocateModal: React.FC<AllocateModalProps> = ({
  open,
  onCancel,
  form,
  onSubmit,
  selectedBooking,
  rooms,
  bookings,
  loading,
}) => {
  useEffect(() => {
    if (open && selectedBooking) {
      if (selectedBooking.ac_status === 'ac' || selectedBooking.ac_status === 'non_ac') {
        form.setFieldValue('ac_selection', selectedBooking.ac_status);
      }
    }
  }, [open, selectedBooking, form]);

  const availableRooms = useMemo(() => {
    return rooms.filter((r) => {
      if (
        r.status === "out_of_order" ||
        r.status === "maintenance" ||
        r.status === "out_of_service" ||
        r.status === "cleaning" ||
        (selectedBooking && r.room_type_id !== selectedBooking.room_type_id)
      ) {
        return false;
      }

      if (selectedBooking && bookings) {
        if (selectedBooking.ac_status === 'ac' && r.ac_status === 'non_ac') return false;
        if (selectedBooking.ac_status === 'non_ac' && r.ac_status === 'ac') return false;

        const sStart = new Date(selectedBooking.check_in_date).getTime();
        const sEnd = new Date(selectedBooking.check_out_date).getTime();

        const isOccupied = bookings.some((b) => {
          if (
            b.id !== selectedBooking.id &&
            b.room_id === r.id &&
            ["pending", "confirmed", "checked_in"].includes(b.status)
          ) {
            const bStart = new Date(b.check_in_date).getTime();
            const bEnd = new Date(b.check_out_date).getTime();
            
            // Overlap check
            return sStart < bEnd && sEnd > bStart;
          }
          return false;
        });

        if (isOccupied) return false;
      }

      return true;
    });
  }, [rooms, selectedBooking, bookings]);

  const maxSelection = selectedBooking && 'quantity' in selectedBooking ? (selectedBooking.quantity as number) : 1;
  const isMulti = maxSelection > 1;

  return (
    <Modal
      open={open}
      title={`Allocate Room${selectedBooking ? ` - ${selectedBooking.guest_name}` : ""}`}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
    >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={(values) => {
          let finalNote = values.note || "";
          if (values.ac_selection) {
             const acText = values.ac_selection === 'ac' ? 'AC Only' : 'Non-AC Only';
             finalNote = finalNote ? `${finalNote}\nRoom Preference: ${acText}` : `Room Preference: ${acText}`;
          }
          onSubmit({ ...values, note: finalNote });
        }}
        initialValues={{ mode: "auto" }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Form.Item
            name="mode"
            label="Allocation Mode"
            rules={[{ required: true }]}
            style={{ marginBottom: 0 }}
          >
            <RadioCardGroup
              options={[
                { label: "Auto Allocate", value: "auto" },
                { label: "Manual Select", value: "manual" },
              ]}
            />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prev, curr) => prev.mode !== curr.mode || prev.room_id !== curr.room_id}
            noStyle
          >
            {({ getFieldValue }) => {
              const mode = getFieldValue("mode");
              const roomId = getFieldValue("room_id");
              const selectedRoom = availableRooms.find((r) => r.id === roomId);

              return mode === "manual" ? (
                <>
                  <Form.Item
                    name={isMulti ? "room_ids" : "room_id"}
                    label={`Select Room${isMulti ? 's' : ''} (Max ${maxSelection})`}
                    rules={[{ required: true, message: `Please select room${isMulti ? 's' : ''}` }]}
                    style={{ marginBottom: 0 }}
                  >
                    {isMulti ? (
                      <CheckboxCardGroup
                        maxSelection={maxSelection}
                        options={availableRooms.map((r) => ({
                          label: `${r.room_number} (Floor ${r.floor}) - ${r.status === "occupied" ? "Occupied" : "Available"}`,
                          value: r.id,
                        }))}
                      />
                    ) : (
                      <RadioCardGroup
                        options={availableRooms.map((r) => ({
                          label: `${r.room_number} (Floor ${r.floor}) - ${r.status === "occupied" ? "Occupied" : "Available"}`,
                          value: r.id,
                        }))}
                      />
                    )}
                  </Form.Item>

                  {selectedRoom && selectedRoom.ac_status === "both" && (
                    <Form.Item
                      name="ac_selection"
                      label="AC / Non-AC Selection"
                      rules={[{ required: true, message: "Please select AC or Non-AC" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <RadioCardGroup
                        options={
                          selectedBooking?.ac_status === 'ac'
                            ? [{ label: "AC Only (Requested)", value: "ac" }]
                            : selectedBooking?.ac_status === 'non_ac'
                            ? [{ label: "Non-AC Only (Requested)", value: "non_ac" }]
                            : [
                                { label: "AC", value: "ac" },
                                { label: "Non-AC", value: "non_ac" },
                              ]
                        }
                      />
                    </Form.Item>
                  )}
                </>
              ) : null;
            }}
          </Form.Item>

          <Form.Item name="note" label="Allocation Note" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={3} placeholder="Any notes regarding this allocation..." />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
